var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import '@/styles/Board/TiptapTextEditor.less';
import html2canvas from 'html2canvas';
import { createElement, useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import InlineTextEditor from '@/components/Board/TextEditor/InlineTextEditor';
/**
 * Хук для рендеринга tiptap контента в изображение через html2canvas.
 * Переиспользует InlineTextEditor с флагом renderMode для единообразия стилей.
 * Создает скрытый DOM элемент, рендерит в него InlineTextEditor и захватывает в canvas.
 *
 * Размер текста масштабируется на основе высоты элемента:
 * - Базовая высота 1000px = коэффициент 1
 * - Высота 500px = коэффициент 0.5 (текст меньше)
 * - Высота 2000px = коэффициент 2 (текст крупнее)
 */
export const useTiptapTextRenderer = ({ element, width, height, renderScale, }) => {
    const [result, setResult] = useState({ url: null, size: null });
    const hiddenRef = useRef(null);
    const rootRef = useRef(null);
    const [editorReady, setEditorReady] = useState(false);
    // Создаем скрытый контейнер для рендеринга
    // Используем InlineTextEditor с флагом renderMode для переиспользования логики
    // Не применяем никаких стилей к контейнеру - все стили применяются через InlineTextEditor
    useEffect(() => {
        const tempDiv = document.createElement('div');
        // Минимальные стили только для скрытия и позиционирования
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-99999px';
        tempDiv.style.top = '0';
        tempDiv.style.width = `${width}px`;
        tempDiv.style.height = `${height}px`;
        // Не применяем overflow, font-family, font-size и другие стили - все берется из редактора
        document.body.appendChild(tempDiv);
        hiddenRef.current = tempDiv;
        // Создаем React root и рендерим InlineTextEditor в режиме рендеринга
        const root = createRoot(tempDiv);
        rootRef.current = root;
        // Создаем элемент с нужными размерами для InlineTextEditor
        const elementForEditor = Object.assign(Object.assign({}, element), { props: Object.assign(Object.assign({}, element.props), { w: width, h: height }) });
        root.render(createElement(InlineTextEditor, {
            element: elementForEditor,
            onSave: () => {
                // Пустая функция, так как в режиме рендеринга сохранение не нужно
            },
            onCancel: () => {
                // Пустая функция, так как в режиме рендеринга отмена не нужна
            },
            stagePosition: { x: 0, y: 0 }, // Позиция не важна для скрытого контейнера
            zoom: 1, // Без zoom для рендеринга
            renderMode: true, // Включаем режим рендеринга
        }));
        // Ждём, пока редактор готов
        let rafId;
        let checkCount = 0;
        const check = () => {
            // Проверяем наличие контента в DOM
            const hasContent = tempDiv.querySelector('.tiptap-text-editor');
            if (hasContent) {
                setEditorReady(true);
            }
            else {
                checkCount++;
                if (checkCount < 100) {
                    rafId = requestAnimationFrame(check);
                }
                else {
                    // Если редактор не готов после 100 проверок, считаем его готовым
                    setEditorReady(true);
                }
            }
        };
        rafId = requestAnimationFrame(check);
        return () => {
            cancelAnimationFrame(rafId);
            if (rootRef.current) {
                rootRef.current.unmount();
            }
            if (tempDiv.parentNode) {
                document.body.removeChild(tempDiv);
            }
            hiddenRef.current = null;
        };
    }, [element, width, height]);
    const renderToCanvas = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!hiddenRef.current)
            return;
        if (!editorReady)
            return;
        // Находим элемент редактора внутри контейнера
        // Захватываем именно элемент с редактором (div с elementStyle), чтобы использовать только его стили
        // В режиме рендеринга это div с position: relative, который содержит редактор
        const editorElement = ((_a = hiddenRef.current
            .querySelector('.tiptap-text-editor')) === null || _a === void 0 ? void 0 : _a.closest('div[style*="position"]')) ||
            hiddenRef.current.querySelector('div[style*="position"]');
        const targetElement = editorElement || hiddenRef.current;
        if (!targetElement)
            return;
        // Ждём шрифты
        if ((_b = document.fonts) === null || _b === void 0 ? void 0 : _b.ready) {
            yield document.fonts.ready;
        }
        // Небольшая задержка для завершения рендеринга
        yield new Promise((resolve) => setTimeout(resolve, 50));
        const scale = renderScale || window.devicePixelRatio || 1;
        try {
            // Захватываем именно элемент редактора, чтобы использовать только его стили
            const canvas = yield html2canvas(targetElement, {
                backgroundColor: null,
                scale,
                useCORS: true,
                logging: false,
                width: targetElement.scrollWidth,
                height: targetElement.scrollHeight,
                windowWidth: targetElement.scrollWidth,
                windowHeight: targetElement.scrollHeight,
            });
            const dataUrl = canvas.toDataURL('image/png');
            setResult({
                url: dataUrl,
                size: { width: canvas.width / scale, height: canvas.height / scale },
            });
        }
        catch (error) {
            console.error('Error rendering tiptap text to image:', error);
            setResult({ url: null, size: null });
        }
    }), [editorReady, renderScale]);
    // Перерисовываем картинку при изменении элемента или размеров
    useEffect(() => {
        if (!editorReady)
            return;
        const id = requestAnimationFrame(() => {
            void renderToCanvas();
        });
        return () => cancelAnimationFrame(id);
    }, [element, width, height, editorReady, renderToCanvas]);
    return result;
};
